import useMobxStores from 'hooks/useMobxStores';
import React from 'react';
import { Link } from 'react-mobx-router5';
import { useTranslation } from 'react-i18next';
import { ToC, HUDMainItemMode, HUDDirection } from '@ole-ui/ole-ui-components';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import ArrowLeftIcon from '@patternfly/react-icons/dist/esm/icons/arrow-left-icon';
import CourseStore from './store';

type Props = {
  courseStore: CourseStore;
  onOverviewButtonClicked: () => void;
  isOverviewDisabled: boolean;
  hudDirection?: HUDDirection;
  mode?: HUDMainItemMode;
  setActiveHUDPanel?: (panel: string | null) => void;
  asSidebar?: boolean;
};

const CourseToC = ({
  courseStore,
  onOverviewButtonClicked,
  isOverviewDisabled,
  hudDirection = HUDDirection.UPWARD,
  mode = HUDMainItemMode.FULL,
  setActiveHUDPanel,
  asSidebar = false,
}: Props) => {
  const { t } = useTranslation();
  const { routerStore } = useMobxStores();
  const { route } = routerStore;

  const {
    courseSlug,
    courseStructure,
    courseTotalProgress,
    isPageBlockedForFreeTierSubscriber,
    pageSlug,
    progressStructure,
    title,
  } = courseStore;

  const renderCourseTitle = () => {
    const content = (
      <Link
        className="hud-course-view__toc-title-link"
        data-analytics-id="ToC-overview-link-ole-lp"
        href
        title={t('Go to course overview')}
        routeName="courses:overview"
        routeParams={{
          course: courseSlug,
        }}
      >
        <ArrowLeftIcon />
        <span>{asSidebar ? t('Overview') : title}</span>
      </Link>
    );

    return <span className="hud-course-view__toc-title">{content}</span>;
  };

  return (
    <ToC
      chapterBorders={asSidebar}
      courseId={courseSlug}
      courseTitle={title}
      renderCourseTitle={renderCourseTitle}
      currentPageUrl={(route as any).params.page}
      direction={hudDirection}
      mainItemMode={mode}
      hasOverview
      isOverviewDisabled={isOverviewDisabled}
      onOverviewButtonClicked={onOverviewButtonClicked}
      progress={courseTotalProgress}
      progressStructure={progressStructure}
      wrapText={asSidebar}
      smallFont={asSidebar}
      separateSearchRow={asSidebar}
      autoFocusSearchInput={!asSidebar}
      renderLink={(structureItem, minimized = false, children = null) => {
        if (isPageBlockedForFreeTierSubscriber(structureItem.page_tag)) {
          return <span>{structureItem.title}</span>;
        }
        return (
          <Link
            data-analytics-id={`toc-link-ole-lp${
              minimized ? '-minimized' : ''
            }`}
            disabled
            href
            onClick={() => {
              if (structureItem.page_tag !== pageSlug && !minimized) {
                setActiveHUDPanel?.(null);
              }
            }}
            routeName="courses:page"
            routeParams={{
              course: (route as any).params.course, // eg. 'le101-1.0' or 'rh124-9.0'
              page: structureItem.page_tag, // eg. 'ch02s03' (legacy) or 'accessing-the-command-line-using-the-desktop' (unique universal id for pages)
            }}
          >
            {children || structureItem.title}
          </Link>
        );
      }}
      structure={courseStructure}
    />
  );
};

export default withErrorBoundary(
  observer(CourseToC),
  ComponentError,
  handleError,
);
